<template>
    <div v-if="showShutdownModal || info.active" class="loading-overlay-container">
        <div class="loading-overlay-modal-background"></div>
        <div class="loading-inner-container" v-bind:class="{ 'is-active': showShutdownModal }">
            <div class="modal-close" @click="closeShutdownModal"></div>
            <div class="modal-card">
                <div class="modal-card-head">
                    <div class="help" @click="toggleHelp">Help<i class="icon">ℹ️</i></div>
                    <div class="textCenter">
                        <h3>Auction Shutdown</h3>
                    </div>
                    <div>&nbsp;</div>
                </div>
                <div class="logFile">
                    <code id="third-party-log-file" title="Read-only" :class="logStyle">
						{{ stream.join("\n") }}
					</code>
                    <div class="metrics-badge">
                        CPU: {{ info.cpuUsage || 0 }}% : MEM: {{ info.memoryUsage || 0 }}%
                    </div>
                    <div class="display-style">
                        <div>
                            <label>
                                <input v-model="logStyle" type="radio" name="log_style" value="classic" />
                                <small>Classic</small>
                            </label>
                        </div>
                        <div>
                            <label>
                                <input v-model="logStyle" type="radio" name="log_style" value="default" />
                                <small>Default</small>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="modal-card-foot">
                    <div class="active-auction-details">
                        <div>
                            <strong>Auction</strong> <br />
                            <span v-if="!!auctionId">{{
                                `(${auctionId}) ${auctionName} `
                                }}</span>
                            <span v-else>No active auction</span>
                        </div>

                        <!-- Third Party EOA Assets -->
                        <div class="marginNormalTop">
                            <strong>Third Party EOA Files</strong>
                            <div>
                                <div class="accordion">
                                    <div v-if="hasLiveAuctioneers" class="accordion-item">
                                        <div class="accordion-header"
                                            @click="liveAuctioneersOpen = !liveAuctioneersOpen">
                                            <span>LiveAuctioneers ({{
                                                liveAuctioneersFileCount
                                                }}
                                                files)</span>
                                            <span class="arrow" :class="{ 'is-open': liveAuctioneersOpen }">▼</span>
                                        </div>
                                        <div v-show="liveAuctioneersOpen" class="accordion-content">
                                            <form class="file-upload-form" @submit.prevent="uploadLiveAuctioneersEOA">
                                                <div class="file-input-wrapper">
                                                    <label for="liveAuctioneersEOAFile">EOA file</label>
                                                    <input id="liveAuctioneersEOAFile" ref="liveAuctioneersFile"
                                                        name="EOA" type="file" accept=".xls,.xlsx,.csv"
                                                        @change="handleLiveAuctioneersFileChange" />
                                                </div>
                                                <button type="submit" class="button is-small"
                                                    :class="{ 'is-success': hasLiveAuctioneersEOAFile }"
                                                    :disabled="!liveAuctioneersEOAFile">
                                                    {{
                                                        hasLiveAuctioneersEOAFile
                                                    ? "File uploaded"
                                                    : "Upload file"
                                                    }}
                                                </button>
                                            </form>
                                            <form class="file-upload-form"
                                                @submit.prevent="uploadLiveAuctioneersBidders">
                                                <div class="file-input-wrapper">
                                                    <label for="liveAuctioneersEOAFile">Winning Bidder Info file</label>
                                                    <input id="liveAuctioneersEOAFile" ref="liveAuctioneersFile"
                                                        name="Bidders" type="file" accept=".xls,.xlsx,.csv"
                                                        @change="handleLiveAuctioneersFileChange" />
                                                </div>
                                                <button type="submit" class="button is-small" :class="{
                                                    'is-success': hasLiveAuctioneersBiddersFile,
                                                }" :disabled="!liveAuctioneersBiddersFile">
                                                    {{
                                                        hasLiveAuctioneersBiddersFile
                                                    ? "File uploaded"
                                                    : "Upload file"
                                                    }}
                                                </button>
                                            </form>
                                        </div>
                                    </div>

                                    <div v-if="hasInvaluable" class="accordion-item">
                                        <div class="accordion-header" @click="invaluableOpen = !invaluableOpen">
                                            <span>Invaluable ({{ invaluableFileCount }}) files</span>
                                            <span class="arrow" :class="{ 'is-open': invaluableOpen }">▼</span>
                                        </div>
                                        <div v-show="invaluableOpen" class="accordion-content">
                                            <!-- TODO: Invaluable content here -->
                                            <p>Invaluable EOA files not implemented yet</p>
                                        </div>
                                    </div>

                                    <div v-if="!hasLiveAuctioneers && !hasInvaluable" class="accordion-item">
                                        <div class="accordion-header">
                                            <span>No files required</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="active-auction-settings">
                        <div class="settings">
                            <div>
                                <strong>Instructions</strong>
                                <div class="instructions">
                                    If required, upload the third party EOA files. Then select the settings for the
                                    auction shutdown process.
                                    The auction will be shut down and the selected settings will be applied once you
                                    click the "Start shutdown" button.
                                    <br>
                                    If you need to stop the shutdown process, click the "Stop shutdown" button. If any
                                    discrepancies are found, the logs will be displayed above, and the shutdown may be
                                    interrupted.
                                </div>
                            </div>
                            <strong>Shutdown Settings</strong>
                            <div class="checkbox-group">
                                <div>
                                    <input v-model="info.settings.invoice_bidders" type="checkbox"
                                        :disabled="startShutdownDisabled">
                                    <span class="instructions">Invoice all bidders</span>
                                </div>
                                <div>
                                    <input v-model="info.settings.email_winning_bidders" type="checkbox"
                                        :disabled="startShutdownDisabled">
                                    <span class="instructions">Email winning bidders</span>
                                </div>
                                <div>
                                    <input v-model="info.settings.email_non_winning_bidders" type="checkbox"
                                        :disabled="startShutdownDisabled">
                                    <span class="instructions">Email non-winning bidders</span>
                                </div>
                                <div>
                                    <input v-model="info.settings.calculate_statistics" type="checkbox"
                                        :disabled="startShutdownDisabled">
                                    <span class="instructions">Calculate auction performance statistics</span>
                                </div>
                                <div>
                                    <input v-model="info.settings.notify_consignors" type="checkbox"
                                        :disabled="startShutdownDisabled">
                                    <span class="instructions">Send consignor notifications</span>
                                </div>
                            </div>
                        </div>
                        <div v-if="info.done === false" class="bottom-controls tabContainer">
                            <button v-if="info.ready_to_finalize === true" class="button"
                                :disabled="info.active === false" @click="finalizeShutdown">Finalize shutdown</button>
                            <button v-if="info.active === false" class="button" :disabled="startShutdownDisabled"
                                @click="startShutdown">Start shutdown</button>
                            <button v-else class="button is-danger" @click="stopShutdown">Stop shutdown</button>
                        </div>
                        <div v-else-if="info.active === false" class="bottom-controls tabContainer loose">
                            <div v-if="info.done === true" class="alert-message">
                                Auction shutdown complete <a class="cursorPointer marginNormalLeft"
                                    @click="exportLogs">Export Logs</a>
                            </div>
                            <button class="button" @click="closeShutdownModal">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <LoadingOverlayComponent v-if="showLoading" v-bind:title="loadingTitle"></LoadingOverlayComponent>
        <AlertComponent v-if="showAlert" v-bind:title="alertTitle" v-bind:message="alertMessage"
            v-bind:secondaryAction="null" @close="closeAlert()">
        </AlertComponent>
    </div>
</template>

<script>
import AlertComponent from "@/components/Shared/AlertComponent.vue";
import LoadingOverlayComponent from "@/components/Shared/LoadingOverlayComponent.vue";

export default {
    name: "AuctionShutdownModal",
    components: {
        AlertComponent,
        LoadingOverlayComponent,
    },
    props: ["activeAuction", "showShutdownModal", "closeShutdownModal"],
    data() {
        return {
            info: {
                active: false,
                ready_to_finalize: false,
                done: false,
                settings: {
                    invoice_bidders: true,
                    email_winning_bidders: true,
                    email_non_winning_bidders: true,
                    calculate_statistics: true,
                    notify_consignors: true,
                },
            },
            stream: [],
            logStyle: "classic",
            liveAuctioneersOpen: false,
            invaluableOpen: false,
            liveAuctioneersEOAFile: null,
            liveAuctioneersBiddersFile: null,
            invaluableEOAFile: null,
            statusEventSource: null,
            logEventSource: null,
            // Loading
            loadingTitle: "Loading...",
            loadingSubTitle: "",
            showLoading: false,
            // Alerts
            showAlert: false,
            alertTitle: "",
            alertMessage: "",
        };
    },
    computed: {
        /**
         * The auction ID
         */
        auctionId() {
            return this.activeAuction?.id || "";
        },
        /**
         * The auction name
         */
        auctionName() {
            return this.activeAuction?.name || "";
        },
        /**
         * Check if the auction has a live auctioneers link
         */
        hasLiveAuctioneers() {
            return !!this.activeAuction?.live_auctioneers_url;
        },
        /**
         * Check if the auction has an invaluable link
         */
        hasInvaluable() {
            return !!this.activeAuction?.invaluable_url;
        },
        /**
         * Get the current invaluable EOA uploaded file count
         */
        invaluableFileCount() {
            return this.info?.eoa_files?.invaluable?.length || 0;
        },
        /**
         * Get the current live auctioneers EOA uploaded file count
         */
        liveAuctioneersFileCount() {
            return this.info?.eoa_files?.liveauctioneers?.length || 0;
        },
        /**
         * Check if the start shutdown button should be disabled
         */
        startShutdownDisabled() {
            if (this.auctionId === "") {
                return true;
            }
            if (this.info.active) {
                return true;
            }
            if (this.hasLiveAuctioneers) {
                return this.liveAuctioneersFileCount < 2; // LA requires 2 files
            }
            if (this.hasInvaluable) {
                return this.invaluableFileCount === 0; // INV requires ? files
            }
            return false;
        },
        /**
         * Check if the LA EOA file has been uploaded
         */
        hasLiveAuctioneersEOAFile() {
            return this.info?.eoa_files?.liveauctioneers?.some(
                (file) =>
                    file.endsWith("EOA.csv") ||
                    file.endsWith("EOA.xls") ||
                    file.endsWith("EOA.xlsx")
            );
        },
        /**
         * Check if the LA Bidders file has been uploaded
         */
        hasLiveAuctioneersBiddersFile() {
            return this.info?.eoa_files?.liveauctioneers?.some(
                (file) =>
                    file.endsWith("Bidders.csv") ||
                    file.endsWith("Bidders.xls") ||
                    file.endsWith("Bidders.xlsx")
            );
        },
    },
    watch: {
        info: {
            handler: function () {
                // If the worker is active, we should subscribe to the logs, if we are not already
                if (this.info.active && !this.logEventSource) {
                    this.subscribeToLogsSSE();
                }
            },
            deep: true,
        },
    },
    mounted: function () {
        this.resetStream();
        if (this.auctionId) {
            this.subscribeToStatusSSE();
        }
    },
    beforeDestroy() {
        this.unsubscribeFromStatusSSE();
        this.unsubscribeFromLogsSSE();
    },
    methods: {
        /**
         * Close the alert
         */
        closeAlert() {
            this.showAlert = false;
            if (this.shouldCloseModalAndNotExit === false) {
                this.exit();
            }
        },

        /**
         * Download the auction shutdown worker log files
         */
        exportLogs() {
            let apiPayload = {};
            apiPayload.apiEndpoint = "active_auction";
            apiPayload.postData = {};
            apiPayload.postData.action = "logs:export:shutdownworker";
            apiPayload.postData.auction_id = this.activeAuction?.id;

            // If there is no active auction id, show an alert
            if (!apiPayload.postData.auction_id) {
                this.alertTitle = "Error";
                this.alertMessage = "No active auction id found";
                this.showAlert = true;
                return;
            }

            this.loadingTitle = "Downloading log file";

            fetch("/api/active_auction/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": this.$store.getters.csrfToken,
                },
                body: JSON.stringify(apiPayload.postData),
            })
                .then((response) => {
                    if (response.ok) return response.blob();
                    this.alertTitle = "Error";
                    switch (response.status) {
                        case 403:
                            this.alertMessage =
                                "You do not have permission to download the log file";
                            break;
                        case 404:
                            this.alertMessage = "The log file could not be found";
                            break;
                        default:
                            this.alertMessage =
                                "An error occurred while downloading the log file";
                    }
                    this.showAlert = true;
                    throw new Error("Network response was not ok");
                })
                .then((blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${this.activeAuction?.id}_shutdown_log.zip`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => console.error("Download error:", error));
        },
        /**
         * Finalize the auction shutdown
         */
        finalizeShutdown() {
            // Send a request to auctionshutdown:finalize endpoint with the auction ID
            let apiPayload = {};
            apiPayload.apiEndpoint = "active_auction";
            apiPayload.postData = {};
            apiPayload.postData.action = "auctionshutdown:finalize";
            apiPayload.postData.auction_id = this.activeAuction?.id;
            this.loadingTitle = "Finalizing auction shutdown";
            this.showLoading = true;
            this.$store
                .dispatch("apiCall", apiPayload)
                .then((response) => {
                    if (response.data.result !== "success") {
                        this.alertTitle = "Error";
                        this.alertMessage = response.data.message;
                        this.showAlert = true;
                    } else {
                    }
                })
                .catch((error) => {
                    console.error("Error finalizing shutdown:", error);
                    this.alertTitle = "Error";
                    this.alertMessage = "Failed to finalize shutdown. Please try again.";
                    this.showAlert = true;
                })
                .finally(() => {
                    setTimeout(() => {
                        this.showLoading = false;
                    }, 1000);
                });
        },
        /**
         * Handle the live auctioneers file change
         */
        handleLiveAuctioneersFileChange(e) {
            switch (e.target.name) {
                case "EOA":
                    this.liveAuctioneersEOAFile = e.target.files[0];
                    break;
                case "Bidders":
                    this.liveAuctioneersBiddersFile = e.target.files[0];
                    break;
            }
        },
        /**
         * TODO: Handle the invaluable file change
         */
        handleInvaluableFileChange(e) {
            this.invaluableEOAFile = e.target.files[0];
        },
        /**
         * Reset the log stream
         */
        resetStream() {
            this.stream = ["// Waiting for logs..."];
        },
        /**
         * Start the auction shutdown process
         */
        startShutdown() {
            this.unsubscribeFromLogsSSE();
            this.resetStream();
            let apiPayload = {};
            apiPayload.apiEndpoint = "active_auction";
            apiPayload.postData = {};
            apiPayload.postData.action = "auctionshutdown:start";
            apiPayload.postData.auction_id = this.activeAuction?.id;
            // We must send the shutdown options
            // create new settings object and convert all true to 'y' and all false to 'n'
            const settings = {};
            for (const [key, value] of Object.entries(this.info.settings)) {
                settings[key] = value ? "y" : "n";
            }
            apiPayload.postData.settings = settings;

            this.loadingTitle = "Starting auction shutdown worker";
            this.showLoading = true;
            this.$store
                .dispatch("apiCall", apiPayload)
                .then((response) => {
                    if (response.data.result !== "success") {
                        this.alertTitle = "Error";
                        this.alertMessage = response.data.message;
                        this.showAlert = true;
                    } else {
                        this.subscribeToLogsSSE();
                    }
                })
                .catch((error) => {
                    console.error("Error starting shutdown worker:", error);
                    this.alertTitle = "Error";
                    this.alertMessage =
                        "Failed to start shutdown worker. Please try again.";
                    this.showAlert = true;
                })
                .finally(() => {
                    setTimeout(() => {
                        this.showLoading = false;
                    }, 1000);
                });
        },
        /**
         * Stop the auction shutdown process
         */
        stopShutdown() {
            let apiPayload = {};
            apiPayload.apiEndpoint = "active_auction";
            apiPayload.postData = {};
            apiPayload.postData.action = "auctionshutdown:stop";
            apiPayload.postData.auction_id = this.activeAuction?.id;

            this.loadingTitle = "Stopping auction shutdown worker";
            this.showLoading = true;
            this.$store
                .dispatch("apiCall", apiPayload)
                .then((response) => {
                    if (response.data.result !== "success") {
                        this.alertTitle = "Error";
                        this.alertMessage = response.data.message;
                        this.showAlert = true;
                    } else {
                        // TODO: something
                    }
                })
                .catch((error) => {
                    console.error("Error stopping shutdown worker:", error);
                    this.alertTitle = "Error";
                    this.alertMessage =
                        "Failed to stop shutdown worker. Please try again.";
                    this.showAlert = true;
                })
                .finally(() => {
                    this.unsubscribeFromLogsSSE();
                    setTimeout(() => {
                        this.showLoading = false;
                    }, 1000);
                });
        },

        /**
         * Subscribe to the SSE for shutdown worker status
         */
        subscribeToStatusSSE() {
            this.statusEventSource = new EventSource(
                `/api/active_auction/?action=auctionshutdown:status&auction_id=${this.activeAuction?.id}`,
                {
                    withCredentials: true,
                }
            );
            this.statusEventSource.onopen = () => {
                console.debug("SSE connection opened");
            };
            this.statusEventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.info = data.data;
                } catch (error) {
                    console.error("Error parsing JSON: ", error);
                }
            };
            this.statusEventSource.onerror = (error) => {
                console.error("SSE error: ", error);
            };
            window.addEventListener("beforeunload", this.unsubscribeFromStatusSSE);
        },

        /**
         * Subscribe to the SSE for shutdown worker logs
         */
        subscribeToLogsSSE() {
            this.logEventSource = new EventSource(
                `/api/active_auction/?action=auctionshutdown:log`,
                {
                    withCredentials: true,
                }
            );
            this.logEventSource.onopen = () => {
                console.debug("SSE connection opened");
            };
            this.logEventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.stream = [...this.stream, ...data.data];

                    // Keep the stream to a maximum of 1000 lines
                    if (this.stream.length > 1000) {
                        this.stream = this.stream.slice(-1000);
                    }

                    // Scroll to the bottom of the code block
                    this.$nextTick(() => {
                        const codeBlock = this.$el.querySelector("code");
                        codeBlock.scrollTop = codeBlock.scrollHeight;
                    });
                } catch (error) {
                    console.error("Error parsing JSON: ", error);
                }
            };
            this.logEventSource.onerror = (error) => {
                console.error("SSE error: ", error);
            };
            window.addEventListener("beforeunload", this.unsubscribeFromLogsSSE);
        },

        /**
         * Toggle the help drawer
         */
        toggleHelp() {
            this.$root.$emit("toggle-help", "user/auction/close.md");
        },

        /**
         * Unsubscribe from the SSE
         */
        unsubscribeFromStatusSSE() {
            if (this.statusEventSource) {
                this.statusEventSource.close();
            }
            window.removeEventListener("beforeunload", this.unsubscribeFromStatusSSE);
        },
        /**
         * Unsubscribe from the SSE
         */
        unsubscribeFromLogsSSE() {
            if (this.logEventSource) {
                this.logEventSource.close();
            }
            window.removeEventListener("beforeunload", this.unsubscribeFromLogsSSE);
        },
        /**
         * Upload the LiveAuctioneers EOA file
         */
        async uploadLiveAuctioneersEOA() {
            if (!this.liveAuctioneersEOAFile) {
                return;
            }

            const extension =
                this.liveAuctioneersEOAFile.type === "text/csv" ? "csv" : "xls";
            const renamedFile = new File(
                [this.liveAuctioneersEOAFile],
                `EOA.${extension}`,
                { type: this.liveAuctioneersEOAFile.type }
            );
            let formData = new FormData();
            formData.append("file", renamedFile);
            formData.append("auction_id", this.activeAuction.id);
            formData.append("action", "auctionshutdown:upload");
            formData.append("third_party", "liveauctioneers");
            formData.append("x_auth_token", $cookies.get("llatoken"));
            try {
                this.loadingTitle = "Uploading LiveAuctioneers EOA file";
                this.showLoading = true;
                const response = await fetch("/api/active_auction/", {
                    method: "POST",
                    body: formData,
                    credentials: "include",
                });

                const data = await response.json();

                if (data.result !== "success") {
                    this.alertTitle = "Error";
                    this.alertMessage = data.message;
                    this.showAlert = true;
                }
            } catch (error) {
                console.error("Upload failed:", error);
                this.alertTitle = "Error";
                this.alertMessage = "Failed to upload file. Please try again.";
                this.showAlert = true;
            } finally {
                setTimeout(() => {
                    this.showLoading = false;
                }, 1000);
            }
        },
        /**
         * Upload the LiveAuctioneers Winning Bidders file
         */
        async uploadLiveAuctioneersBidders() {
            if (!this.liveAuctioneersBiddersFile) {
                return;
            }
            const extension =
                this.liveAuctioneersBiddersFile.type === "text/csv" ? "csv" : "xls";
            const renamedFile = new File(
                [this.liveAuctioneersBiddersFile],
                `Bidders.${extension}`,
                { type: this.liveAuctioneersBiddersFile.type }
            );
            let formData = new FormData();
            formData.append("file", renamedFile);
            formData.append("auction_id", this.activeAuction.id);
            formData.append("action", "auctionshutdown:upload");
            formData.append("third_party", "liveauctioneers");
            formData.append("x_auth_token", $cookies.get("llatoken"));
            try {
                this.loadingTitle = "Uploading LiveAuctioneers Winning Bidders file";
                this.showLoading = true;
                const response = await fetch("/api/active_auction/", {
                    method: "POST",
                    body: formData,
                    credentials: "include",
                });

                const data = await response.json();

                if (data.result !== "success") {
                    this.alertTitle = "Error";
                    this.alertMessage = data.message;
                    this.showAlert = true;
                }
            } catch (error) {
                console.error("Upload failed:", error);
                this.alertTitle = "Error";
                this.alertMessage = "Failed to upload file. Please try again.";
                this.showAlert = true;
            } finally {
                setTimeout(() => {
                    this.showLoading = false;
                }, 1000);
            }
        },
    },
};
</script>

<style scoped>
.loading-inner-container {
    width: 90vw;
    max-width: 1200px;
    padding: 1.5rem 0;
}

.modal-close {
    top: 1.2rem !important;
    right: 1.2rem !important;
}

.modal-card {
    width: 100%;
    height: 80vh;
    padding: 1rem 2rem;
    display: flex;
    flex-direction: column;
}

.modal-card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 1rem;
}

.modal-card-head>div {
    flex: 1;
}

.modal-card-foot {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: start;
    padding: 0 1rem;
    gap: 2rem;
}

.active-auction-details {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
}

.active-auction-settings {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
}

.active-auction-settings .settings {
    flex: 1;
}

.logFile {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 50%;
    width: 100%;
    padding: 1rem;
    align-items: end;
}

.logFile code {
    height: 100%;
    width: 100%;
    overflow-y: scroll;
    word-wrap: break-word;
    white-space: pre-line;
    line-height: 100%;
    padding: 1rem;
    scrollbar-width: thin;
    scrollbar-color: #888 #f1f1f1;
}

.logFile code.classic {
    background-color: #1e1e1e;
    color: #c7c7c7;
    font-family: Menlo, Monaco, "Courier New", monospace;
}

.logFile code::-webkit-scrollbar {
    width: 8px;
}

.logFile code::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.logFile code::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 4px;
    border: 2px solid #f1f1f1;
}

.logFile code::-webkit-scrollbar-thumb:hover {
    background-color: #555;
}

.logFile .metrics-badge {
    position: absolute;
    bottom: 50px;
    right: 35px;
    background-color: transparent;
    padding: 0 0.5em;
    font-size: 10px;
    border-radius: 10px;
}

.logFile .metrics-badge {
    background-color: limegreen;
    color: white;
    line-height: 150%;
}

.button {
    background-color: #007aff;
    color: white;
    border: 1px solid #007aff;
    border-radius: 4px;
}

.button.is-small {
    padding: 0.5rem 1rem;
    font-size: 1.4rem;
    height: 3rem;
    line-height: 100%;
}

.button.is-danger {
    background-color: #ff3b30;
    border: 1px solid #ff3b30;
}

.button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.button.is-success {
    background-color: #28a745;
    border-color: #28a745;
    color: white;
}

.accordion-item {
    border: 1px solid #ddd;
    margin-bottom: 5px;
}

.accordion-header {
    padding: 10px;
    background: #f5f5f5;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
}

.accordion-content {
    padding: 10px;
}

.arrow {
    transition: transform 0.3s ease;
}

.arrow.is-open {
    transform: rotate(180deg);
}

form {
    margin-bottom: 0;
}

.file-upload-form {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: start;
}

.bottom-controls {
    height: 100%;
    align-items: flex-end;
    gap: 1rem;
}

.bottom-controls.loose {
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
}

.logFile .display-style {
    display: flex;
    gap: 0.5em;
    padding-right: 2rem;
}

.logFile .display-style label {
    display: flex;
    gap: 0.25em;
    cursor: pointer;
}

.instructions {
    margin-bottom: 1rem;
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 115%;
    padding-left: 1%;
}

.alert-message {
    padding: 1rem;
    background-color: #f1f1f1;
    border-radius: 4px;
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 115%;
}

.help {
    cursor: pointer;
}

.help .icon {
    margin-left: 5px;
}
</style>
